import { useState, useMemo } from 'react';
import {
  Button, Select, SelectItem, Input, Chip, Card, CardBody, CardHeader,
  Table, TableHeader, TableColumn, TableBody, TableRow, TableCell,
  Divider, Spinner,
} from '@heroui/react';
import { Truck, Save, Trash2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import {
  usePackTrucks, useTruckConfigs, useSaveTruckConfig, useDeleteTruckConfig,
  type PackResponse, type TruckResult,
} from '@/api/truck-loader';
import TruckScene from './TruckScene';

interface TruckLoaderTabProps {
  projectId: string;
  projectName?: string;
}

const TRUCK_TYPES = [
  { key: '24_box', label: "24' Box Truck" },
  { key: '53_trailer', label: "53' Trailer" },
];

const PRIORITY_MODES = [
  { key: 'efficiency', label: 'Best Efficiency' },
  { key: 'by_area', label: 'Group by Area' },
  { key: 'keep_areas_together', label: 'Keep Areas Together' },
  { key: 'group_by_phase', label: 'Group by Phase' },
];

function printManifest(truck: TruckResult, truckIndex: number, projectName: string) {
  const rows = truck.items
    .map(
      (item, i) =>
        `<tr>
          <td>${i + 1}</td>
          <td>${item.description}</td>
          <td>${item.areaName}</td>
          <td>${item.width}"×${item.height}"×${item.depth}"</td>
          <td>${item.weight.toFixed(1)}</td>
          <td>(${item.x.toFixed(0)}, ${item.y.toFixed(0)}, ${item.z.toFixed(0)})</td>
        </tr>`,
    )
    .join('');

  const html = `<!DOCTYPE html><html><head><title>Truck ${truckIndex + 1} Manifest</title>
<style>body{font-family:Arial,sans-serif;margin:20px}table{width:100%;border-collapse:collapse;margin-top:12px}
th,td{border:1px solid #ccc;padding:6px 8px;text-align:left;font-size:13px}th{background:#f5f5f5}
h2{margin-bottom:4px}.meta{color:#666;font-size:13px;margin-bottom:12px}
.totals{margin-top:12px;font-weight:bold}</style></head>
<body><h2>Loading Manifest — Truck ${truckIndex + 1}</h2>
<div class="meta">Project: ${projectName} | Date: ${new Date().toLocaleDateString()}</div>
<table><thead><tr><th>#</th><th>Description</th><th>Area</th><th>Dimensions</th><th>Weight (lbs)</th><th>Position</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="totals">Items: ${truck.items.length} | Weight: ${truck.weight.toFixed(0)} lbs | Fill: ${truck.fillPct.toFixed(1)}%</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (w) {
    w.document.write(html);
    w.document.close();
    w.print();
  }
}

export default function TruckLoaderTab({ projectId, projectName = 'Project' }: TruckLoaderTabProps) {
  const [truckType, setTruckType] = useState('24_box');
  const [maxStack, setMaxStack] = useState(2);
  const [padding, setPadding] = useState(2);
  const [priority, setPriority] = useState('efficiency');
  const [costPerTruck, setCostPerTruck] = useState(0);
  const [importCost, setImportCost] = useState(0);
  const [tariffCost, setTariffCost] = useState(0);
  const [colorMode, setColorMode] = useState<'type' | 'area' | 'phase'>('type');
  const [result, setResult] = useState<PackResponse | null>(null);
  const [saveName, setSaveName] = useState('');

  const packMutation = usePackTrucks();
  const { data: savedConfigs } = useTruckConfigs(projectId);
  const saveMutation = useSaveTruckConfig();
  const deleteMutation = useDeleteTruckConfig();

  const handlePack = () => {
    packMutation.mutate(
      { projectId, truckType, maxStack, padding, priority },
      {
        onSuccess: (data) => {
          setResult(data);
          toast.success(`Packed ${data.totalItems} items into ${data.trucks.length} truck(s)`);
        },
        onError: (err: any) => toast.error(err?.message || 'Packing failed'),
      },
    );
  };

  const handleSave = () => {
    if (!result || !saveName.trim()) return;
    saveMutation.mutate(
      {
        projectId,
        name: saveName.trim(),
        config: JSON.stringify({ truckType, maxStack, padding, priority }),
        trucksData: JSON.stringify(result.trucks),
        costPerTruck,
        importCost,
        tariffCost,
      },
      {
        onSuccess: () => {
          toast.success('Config saved');
          setSaveName('');
        },
      },
    );
  };

  const areaNames = useMemo(() => {
    if (!result) return [];
    return [...new Set(result.trucks.flatMap(t => t.items.map(i => i.areaName)))];
  }, [result]);

  const phaseNames = useMemo(() => {
    if (!result) return [];
    return [...new Set(result.trucks.flatMap(t => t.items.map(i => i.phaseName)).filter(Boolean))];
  }, [result]);

  const totalWeight = result?.trucks.reduce((s, t) => s + t.weight, 0) ?? 0;
  const avgFill =
    result && result.trucks.length > 0
      ? result.trucks.reduce((s, t) => s + t.fillPct, 0) / result.trucks.length
      : 0;
  const totalItemsPlaced = result?.trucks.reduce((s, t) => s + t.items.length, 0) ?? 0;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <Card>
        <CardBody className="gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <Select
              label="Truck Type"
              selectedKeys={[truckType]}
              onSelectionChange={(keys) => setTruckType([...keys][0] as string)}
              className="w-44"
              size="sm"
            >
              {TRUCK_TYPES.map(t => (
                <SelectItem key={t.key}>{t.label}</SelectItem>
              ))}
            </Select>
            <Input
              type="number"
              label="Max Stack"
              value={String(maxStack)}
              onValueChange={(v) => setMaxStack(Math.min(5, Math.max(1, Number(v) || 1)))}
              className="w-24"
              size="sm"
            />
            <Input
              type="number"
              label="Padding (in)"
              value={String(padding)}
              onValueChange={(v) => setPadding(Math.min(12, Math.max(0, Number(v) || 0)))}
              className="w-28"
              size="sm"
            />
            <Select
              label="Priority"
              selectedKeys={[priority]}
              onSelectionChange={(keys) => setPriority([...keys][0] as string)}
              className="w-48"
              size="sm"
            >
              {PRIORITY_MODES.map(p => (
                <SelectItem key={p.key}>{p.label}</SelectItem>
              ))}
            </Select>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Input
              type="number"
              label="Cost/Truck ($)"
              value={String(costPerTruck)}
              onValueChange={(v) => setCostPerTruck(Number(v) || 0)}
              className="w-32"
              size="sm"
            />
            <Input
              type="number"
              label="Import ($)"
              value={String(importCost)}
              onValueChange={(v) => setImportCost(Number(v) || 0)}
              className="w-28"
              size="sm"
            />
            <Input
              type="number"
              label="Tariff ($)"
              value={String(tariffCost)}
              onValueChange={(v) => setTariffCost(Number(v) || 0)}
              className="w-28"
              size="sm"
            />
            <Button
              color="primary"
              onPress={handlePack}
              isLoading={packMutation.isPending}
              startContent={<Truck size={16} />}
            >
              Calculate Loading
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Stats */}
      {result && (
        <div className="flex flex-wrap gap-3">
          <Chip variant="flat" color="primary">Trucks: {result.trucks.length}</Chip>
          <Chip variant="flat" color="default">Weight: {totalWeight.toFixed(0)} lbs</Chip>
          <Chip variant="flat" color={avgFill >= 80 ? 'success' : avgFill >= 50 ? 'warning' : 'danger'}>
            Avg Fill: {avgFill.toFixed(1)}%
          </Chip>
          <Chip variant="flat" color="default">Items: {totalItemsPlaced}</Chip>
          {result.skippedItems > 0 && (
            <Chip variant="flat" color="warning">Skipped: {result.skippedItems} (missing dims)</Chip>
          )}
        </div>
      )}

      {/* Color mode selector */}
      {result && result.trucks.length > 0 && (
        <div className="flex gap-2 items-center">
          <span className="text-sm text-default-500">Color by:</span>
          {(['type', 'area', 'phase'] as const).map(mode => (
            <Button
              key={mode}
              size="sm"
              color={colorMode === mode ? 'primary' : 'default'}
              variant={colorMode === mode ? 'solid' : 'flat'}
              onPress={() => setColorMode(mode)}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)}
            </Button>
          ))}
        </div>
      )}

      {/* Per-truck cards */}
      {result?.trucks.map((truck, idx) => (
        <Card key={idx}>
          <CardHeader className="flex justify-between items-center">
            <div className="flex gap-3 items-center">
              <span className="font-semibold">Truck {idx + 1}</span>
              {truck.phase && <Chip size="sm" variant="flat">{truck.phase}</Chip>}
              <span className="text-sm text-default-500">
                {truck.weight.toFixed(0)} lbs | {truck.fillPct.toFixed(1)}% fill | {truck.items.length} items
              </span>
            </div>
            <Button
              size="sm"
              variant="flat"
              startContent={<Printer size={14} />}
              onPress={() => printManifest(truck, idx, projectName)}
            >
              Print Manifest
            </Button>
          </CardHeader>
          <CardBody className="gap-4">
            <TruckScene
              truck={truck}
              truckSpec={result.truckSpec}
              colorMode={colorMode}
              areaNames={areaNames}
              phaseNames={phaseNames}
            />
            <Divider />
            <Table aria-label={`Truck ${idx + 1} manifest`} isCompact removeWrapper>
              <TableHeader>
                <TableColumn>Description</TableColumn>
                <TableColumn>Area</TableColumn>
                <TableColumn>Dimensions</TableColumn>
                <TableColumn>Weight</TableColumn>
                <TableColumn>Position</TableColumn>
              </TableHeader>
              <TableBody>
                {truck.items.map((item, i) => (
                  <TableRow key={`${item.id}-${item.instance}-${i}`}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>{item.areaName}</TableCell>
                    <TableCell>{item.width}"×{item.height}"×{item.depth}"</TableCell>
                    <TableCell>{item.weight.toFixed(1)} lbs</TableCell>
                    <TableCell>({item.x.toFixed(0)}, {item.y.toFixed(0)}, {item.z.toFixed(0)})</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardBody>
        </Card>
      ))}

      {/* Save / Load Configs */}
      {result && (
        <Card>
          <CardBody className="gap-3">
            <div className="flex gap-2 items-end">
              <Input
                label="Config Name"
                value={saveName}
                onValueChange={setSaveName}
                size="sm"
                className="max-w-xs"
              />
              <Button
                size="sm"
                color="primary"
                variant="flat"
                startContent={<Save size={14} />}
                onPress={handleSave}
                isDisabled={!saveName.trim()}
                isLoading={saveMutation.isPending}
              >
                Save
              </Button>
            </div>
            {savedConfigs && savedConfigs.length > 0 && (
              <>
                <Divider />
                <div className="text-sm font-semibold">Saved Configs</div>
                {savedConfigs.map(cfg => (
                  <div key={cfg.id} className="flex items-center gap-2 text-sm">
                    <span>{cfg.name}</span>
                    <span className="text-default-400">
                      {new Date(cfg.created_at).toLocaleDateString()}
                    </span>
                    <Button
                      size="sm"
                      variant="light"
                      color="danger"
                      isIconOnly
                      onPress={() =>
                        deleteMutation.mutate(
                          { configId: cfg.id, projectId },
                          { onSuccess: () => toast.success('Deleted') },
                        )
                      }
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </>
            )}
          </CardBody>
        </Card>
      )}

      {/* Loading state */}
      {packMutation.isPending && (
        <div className="flex justify-center p-8">
          <Spinner label="Calculating truck loading..." />
        </div>
      )}
    </div>
  );
}
